// src/pages/Admin.js
import React, { useState, useEffect } from 'react';
import { collection, addDoc, deleteDoc, doc, updateDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import ServiceForm from '../components/admin/ServiceForm';
import { formatPriceLabel } from '../utils/helpers';

const Admin = () => {
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pobieranie usług
  useEffect(() => {
    const servicesRef = collection(db, 'services');
    
    const unsubscribe = onSnapshot(servicesRef, 
      (snapshot) => {
        const servicesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setServices(servicesData);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching services:', error);
        setError(error.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // Dodawanie nowej usługi
  const handleAddService = async (serviceData) => {
    try {
      const servicesRef = collection(db, 'services');
      await addDoc(servicesRef, {
        ...serviceData,
        createdAt: new Date().toISOString()
      });
      setSelectedService(null);
    } catch (error) {
      console.error('Error adding service:', error);
      setError(error.message);
    }
  };

  // Aktualizacja usługi
  const handleUpdateService = async (id, serviceData) => {
    try {
      const serviceRef = doc(db, 'services', id);
      await updateDoc(serviceRef, {
        ...serviceData,
        updatedAt: new Date().toISOString()
      });
      setSelectedService(null);
    } catch (error) {
      console.error('Error updating service:', error);
      setError(error.message);
    }
  };

  // Usuwanie usługi
  const handleDeleteService = async (id) => {
    if (window.confirm('Czy na pewno chcesz usunąć tę usługę?')) {
      try {
        const serviceRef = doc(db, 'services', id);
        await deleteDoc(serviceRef);
      } catch (error) {
        console.error('Error deleting service:', error);
        setError(error.message);
      }
    }
  };

  const handleSubmit = async (formData) => {
    if (selectedService) {
      await handleUpdateService(selectedService.id, formData);
    } else {
      await handleAddService(formData);
    }
  };

  return (
    <div className="relative min-h-screen pt-20">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 to-black/50 z-10" />
        <img
          src="https://images.unsplash.com/photo-1672840997353-4579d5d6a7d5?q=80&w=1932&auto=format&fit=crop"
          alt=""
          className="w-full h-full object-cover object-center fixed"
          aria-hidden="true"
        />
      </div>

      <div className="relative z-20 container mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold text-white mb-8">Panel administracyjny</h1>

        {error && (
          <div className="bg-red-500/10 border border-red-500 text-red-500 p-4 rounded-lg mb-8">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Lista usług */}
          <div className="bg-black/30 backdrop-blur-sm rounded-2xl p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Lista usług</h2>
            <div className="space-y-4">
              {loading ? (
                <p className="text-white">Ładowanie...</p>
              ) : (
                services.map(service => (
                  <div key={service.id} className="bg-white/5 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-medium text-white">{service.title}</h3>
                        <p className="text-gray-300">{service.category === 'Pakiety'
                            ? `Osobowe: ${formatPriceLabel(service.personalPrice)} / SUV/Van: ${formatPriceLabel(service.suvPrice)}`
                            : formatPriceLabel(service.price)} - {service.duration} min</p>
                      </div>
                      <div className="space-x-2">
                        <button
                          onClick={() => setSelectedService(service)}
                          className="px-3 py-1 bg-blue-500/20 hover:bg-blue-500/30 text-white rounded-md"
                        >
                          Edytuj
                        </button>
                        <button
                          onClick={() => handleDeleteService(service.id)}
                          className="px-3 py-1 bg-red-500/20 hover:bg-red-500/30 text-white rounded-md"
                        >
                          Usuń
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Formularz */}
          <div className="bg-black/30 backdrop-blur-sm rounded-2xl p-6">
            <h2 className="text-xl font-semibold text-white mb-4">
              {selectedService ? 'Edytuj usługę' : 'Dodaj nową usługę'}
            </h2>
            <ServiceForm
              initialData={selectedService}
              onSubmit={handleSubmit}
              onCancel={() => setSelectedService(null)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin;